<template>
    <main class="grouping-history">
        <header class="grouping-history__header">
            <button type="button" class="icon-button" title="Voltar ao agrupamento" @click="back">
                <ArrowLeftIcon />
            </button>
            <div class="grouping-history__identity">
                <div>
                    <h1>Histórico de apontamentos</h1>
                    <span v-if="grouping" class="code-pill">{{ grouping.code }}</span>
                </div>
                <p v-if="grouping">
                    {{ typeLabel(grouping.type) }} · {{ grouping.resourceName }}
                </p>
            </div>
        </header>

        <div v-if="loading" class="state-message">
            <LoaderIcon class="spin" /> Carregando histórico
        </div>
        <div v-else-if="error" class="state-message state-message--error">
            <AlertCircleIcon /> {{ error }}
            <button type="button" @click="load">Tentar novamente</button>
        </div>

        <template v-else>
            <section class="history-summary" aria-label="Resumo dos apontamentos">
                <div>
                    <small>Apontamentos</small>
                    <strong>{{ history.length }}</strong>
                </div>
                <div>
                    <small>Produzido</small>
                    <strong>{{ formatNumber(totalProduced) }}</strong>
                </div>
                <div>
                    <small>Retrabalho</small>
                    <strong>{{ formatNumber(totalRework) }}</strong>
                </div>
                <div>
                    <small>Refugo</small>
                    <strong>{{ formatNumber(totalRefuse) }}</strong>
                </div>
            </section>

            <section class="history-panel">
                <header>
                    <div>
                        <h2>Registros do agrupamento</h2>
                        <p>Abra um apontamento para consultar as quantidades de cada ordem.</p>
                    </div>
                    <span>{{ history.length }} registros</span>
                </header>

                <div v-if="!history.length" class="empty-history">
                    <ClipboardIcon />
                    <strong>Nenhum apontamento registrado</strong>
                    <span>Os próximos apontamentos aparecerão nesta tela.</span>
                </div>

                <div v-else class="history-list">
                    <article
                        v-for="appointment in paginatedHistory"
                        :key="appointment.id"
                        class="history-entry"
                    >
                        <div class="history-entry__toolbar">
                            <button
                                type="button"
                                class="history-entry__summary"
                                :aria-expanded="isExpanded(appointment.id)"
                                @click="toggleHistory(appointment.id)"
                            >
                            <span class="history-entry__date">
                                <small>Data e hora</small>
                                <strong>{{ formatDateTime(appointment.eventTime) }}</strong>
                            </span>
                            <span class="history-entry__metric">
                                <small>Produzido</small>
                                <strong>{{ formatNumber(appointment.totalProducedQuantity) }}</strong>
                            </span>
                            <span class="history-entry__metric">
                                <small>Retrabalho</small>
                                <strong>{{ formatNumber(appointment.totalReworkQuantity) }}</strong>
                            </span>
                            <span class="history-entry__metric">
                                <small>Refugo</small>
                                <strong>{{ formatNumber(appointment.totalRefuseQuantity) }}</strong>
                            </span>
                            <span class="history-entry__count">
                                {{ appointment.orders.length }} OPs
                            </span>
                                <ChevronUpIcon v-if="isExpanded(appointment.id)" />
                                <ChevronDownIcon v-else />
                            </button>
                            <button
                                type="button"
                                class="history-entry__delete"
                                title="Excluir apontamento"
                                :disabled="deletingId === appointment.id"
                                @click="openDeleteAppointmentModal(appointment)"
                            >
                                <LoaderIcon v-if="deletingId === appointment.id" class="spin" />
                                <Trash2Icon v-else />
                            </button>
                        </div>

                        <div v-if="isExpanded(appointment.id)" class="history-entry__orders">
                            <div class="history-order history-order--header">
                                <span>Ordem / Operação</span>
                                <span>Produzido</span>
                                <span>Retrabalho</span>
                                <span>Refugo</span>
                                <span>Motivo</span>
                            </div>
                            <div
                                v-for="order in appointment.orders"
                                :key="`${appointment.id}-${order.operationId}`"
                                class="history-order"
                            >
                                <span>
                                    <strong>{{ order.orderNumber }}</strong>
                                    <small>Op. {{ order.operationNumber }} · {{ order.operationName }}</small>
                                </span>
                                <span data-label="Produzido">{{ formatNumber(order.producedQuantity) }}</span>
                                <span data-label="Retrabalho">{{ formatNumber(order.reworkQuantity) }}</span>
                                <span data-label="Refugo">{{ formatNumber(order.refuseQuantity) }}</span>
                                <span data-label="Motivo">{{ order.refuseReasonName || "-" }}</span>
                            </div>
                        </div>
                    </article>
                </div>

                <footer v-if="history.length" class="history-pagination">
                    <label>
                        Exibir
                        <select v-model.number="pageSize" @change="page = 1">
                            <option :value="5">5</option>
                            <option :value="10">10</option>
                        </select>
                    </label>
                    <span>Página {{ page }} de {{ totalPages }}</span>
                    <button
                        type="button"
                        title="Página anterior"
                        :disabled="page === 1"
                        @click="page -= 1"
                    ><ChevronLeftIcon /></button>
                    <button
                        type="button"
                        title="Próxima página"
                        :disabled="page === totalPages"
                        @click="page += 1"
                    ><ChevronRightIcon /></button>
                </footer>
            </section>
        </template>

        <OrderGroupingActionModal
            :open="deleteModalOpen"
            title="Excluir apontamento?"
            eyebrow="Histórico do agrupamento"
            description="O registro será removido do MySFC. Uma confirmação já aceita pelo SAP não será desfeita."
            tone="warning"
            confirm-label="Excluir apontamento"
            cancel-label="Voltar"
            :busy="Boolean(deletingId)"
            @cancel="closeDeleteAppointmentModal"
            @confirm="deleteAppointment"
        >
            <Trash2Icon slot="icon" />
            <div class="modal-notice">
                Esta ação remove apenas o registro do MySFC e não envia estorno ao SAP.
            </div>
        </OrderGroupingActionModal>

        <OrderGroupingActionModal
            :open="reopenedModalOpen"
            title="Agrupamento reaberto"
            eyebrow="Status atualizado"
            description="A quantidade voltou a ficar abaixo do previsto. As ordens retornaram para Execução e podem ser apontadas novamente."
            tone="success"
            confirm-label="Entendi"
            :show-cancel="false"
            @cancel="closeReopenedModal"
            @confirm="closeReopenedModal"
        >
            <CheckCircleIcon slot="icon" />
            <div class="modal-notice modal-notice--success">
                O próximo apontamento pode ser feito normalmente pela tela do agrupamento.
            </div>
        </OrderGroupingActionModal>
    </main>
</template>

<script>
import {
    AlertCircleIcon,
    ArrowLeftIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronUpIcon,
    CheckCircleIcon,
    ClipboardIcon,
    LoaderIcon,
    Trash2Icon
} from "vue-feather-icons";
import createOrderGroupingApi from "./orderGroupingApi";
import { apiErrorMessage, typeLabel } from "./groupingPresentation";
import OrderGroupingActionModal from "./OrderGroupingActionModal.vue";

export default {
    components: {
        AlertCircleIcon,
        ArrowLeftIcon,
        ChevronDownIcon,
        ChevronLeftIcon,
        ChevronRightIcon,
        ChevronUpIcon,
        CheckCircleIcon,
        ClipboardIcon,
        LoaderIcon,
        OrderGroupingActionModal,
        Trash2Icon
    },
    data() {
        return {
            grouping: null,
            history: [],
            loading: false,
            error: "",
            deletingId: null,
            deleteModalOpen: false,
            reopenedModalOpen: false,
            selectedAppointment: null,
            page: 1,
            pageSize: 5,
            expanded: {}
        };
    },
    computed: {
        totalProduced() {
            return this.history.reduce((total, item) => total + Number(item.totalProducedQuantity || 0), 0);
        },
        totalRework() {
            return this.history.reduce((total, item) => total + Number(item.totalReworkQuantity || 0), 0);
        },
        totalRefuse() {
            return this.history.reduce((total, item) => total + Number(item.totalRefuseQuantity || 0), 0);
        },
        totalPages() {
            return Math.max(Math.ceil(this.history.length / this.pageSize), 1);
        },
        paginatedHistory() {
            const start = (this.page - 1) * this.pageSize;
            return this.history.slice(start, start + this.pageSize);
        }
    },
    created() {
        this.load();
    },
    methods: {
        typeLabel,
        async load() {
            this.loading = true;
            this.error = "";
            try {
                const api = createOrderGroupingApi(this.$http2);
                const [grouping, history] = await Promise.all([
                    api.get(this.$route.params.id),
                    api.appointments(this.$route.params.id)
                ]);
                this.grouping = grouping;
                this.history = history || [];
                this.page = 1;
            } catch (error) {
                this.error = apiErrorMessage(error);
            } finally {
                this.loading = false;
            }
        },
        back() {
            this.$router.push({
                name: "operator-order-grouping-detail",
                params: { prefix: this.$route.params.prefix, id: this.$route.params.id }
            });
        },
        formatNumber(value) {
            return Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
        },
        formatDateTime(value) {
            if (!value) return "-";
            return new Date(value).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            });
        },
        isExpanded(id) {
            return Boolean(this.expanded[id]);
        },
        toggleHistory(id) {
            this.$set(this.expanded, id, !this.expanded[id]);
        },
        openDeleteAppointmentModal(appointment) {
            if (this.deletingId) return;
            this.selectedAppointment = appointment;
            this.deleteModalOpen = true;
        },
        closeDeleteAppointmentModal() {
            if (this.deletingId) return;
            this.deleteModalOpen = false;
            this.selectedAppointment = null;
        },
        closeReopenedModal() {
            this.reopenedModalOpen = false;
        },
        async deleteAppointment() {
            if (this.deletingId || !this.selectedAppointment) return;

            this.deletingId = this.selectedAppointment.id;
            try {
                const result = await createOrderGroupingApi(this.$http2).deleteAppointment(
                    this.$route.params.id,
                    this.selectedAppointment.id
                );
                await this.load();
                this.deleteModalOpen = false;
                this.selectedAppointment = null;
                this.reopenedModalOpen = Boolean(result?.reopened);
            } catch (error) {
                await this.$swal({
                    title: "Não foi possível excluir",
                    text: apiErrorMessage(error),
                    icon: "error",
                    confirmButtonText: "Fechar"
                });
            } finally {
                this.deletingId = null;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.grouping-history {
    width: 100%;
    color: #4c4541;

    &__header {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 16px;
    }

    &__identity {
        min-width: 0;

        > div { display: flex; align-items: center; gap: 9px; }
        h1 { margin: 0; font-family: "Raleway"; font-size: 22px; font-weight: 600; }
        p { margin: 3px 0 0; color: #7e7570; }
    }
}

.icon-button {
    width: 44px;
    height: 44px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border: 1px solid #cfc4be;
    border-radius: 5px;
    background: #fff;
    color: #974900;
}

.code-pill {
    display: inline-flex;
    min-height: 22px;
    align-items: center;
    border-radius: 11px;
    padding: 2px 9px;
    background: #fff1e8;
    color: #974900;
    font-family: monospace;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
}

.history-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(120px, 1fr));
    border: 1px solid #d8d6de;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;

    > div {
        min-height: 78px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 10px;
        text-align: center;

        + div { border-left: 1px solid #ece0db; }
    }

    small { margin-bottom: 5px; color: #998f8a; }
    strong { font-size: 17px; }
}

.history-panel {
    margin-top: 14px;
    border: 1px solid #d8d6de;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;

    > header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 16px;
        border-bottom: 1px solid #ece0db;

        h2 { margin: 0; font-family: "Raleway"; font-size: 16px; font-weight: 600; }
        p { margin: 3px 0 0; color: #998f8a; font-size: 12px; }
        > span { color: #7e7570; font-size: 12px; white-space: nowrap; }
    }
}

.empty-history {
    min-height: 220px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 24px;
    color: #7e7570;
    text-align: center;

    svg { width: 28px; height: 28px; color: #974900; }
    span { color: #998f8a; font-size: 12px; }
}

.history-entry + .history-entry { border-top: 1px solid #ece0db; }

.history-entry__toolbar {
    display: flex;
    align-items: stretch;
}

.history-entry__summary {
    min-width: 0;
    flex: 1;
    min-height: 76px;
    display: grid;
    grid-template-columns: minmax(190px, 1.4fr) repeat(3, minmax(86px, 0.65fr)) auto 24px;
    align-items: center;
    gap: 14px;
    padding: 12px 16px;
    border: 0;
    background: #fff;
    color: #4c4541;
    text-align: left;

    &:hover { background: #fbfaf9; }
    > svg { width: 20px; height: 20px; color: #974900; }
}

.history-entry__delete {
    width: 52px;
    flex: 0 0 52px;
    display: grid;
    place-items: center;
    margin: 12px 12px 12px 0;
    border: 1px solid #e4b8b4;
    border-radius: 5px;
    background: #fff;
    color: #b42318;

    &:hover { background: #fff0ef; }
    &:disabled { opacity: 0.45; }
    svg { width: 19px; height: 19px; }
}

.history-entry__date,
.history-entry__metric {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;

    small { color: #998f8a; font-size: 10px; text-transform: uppercase; }
    strong { overflow: hidden; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
}

.history-entry__count {
    border-radius: 11px;
    padding: 3px 9px;
    background: #fff1e8;
    color: #974900;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.history-entry__orders {
    padding: 0 16px 14px;
    background: #fbfaf9;
}

.history-order {
    display: grid;
    grid-template-columns: minmax(190px, 1.4fr) repeat(3, minmax(86px, 0.65fr)) minmax(110px, 1fr);
    gap: 14px;
    padding: 10px 0;
    border-top: 1px solid #ece0db;
    font-size: 12px;

    > span:first-child { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    strong, small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    small { color: #7e7570; }

    &--header {
        border-top: 0;
        color: #998f8a;
        font-size: 10px;
        text-transform: uppercase;
    }
}

.history-pagination {
    min-height: 58px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 8px 16px;
    border-top: 1px solid #ece0db;
    color: #7e7570;
    font-size: 12px;

    label { display: flex; align-items: center; gap: 7px; }
    select { min-height: 38px; border: 1px solid #cfc4be; border-radius: 4px; background: #fff; }
    button {
        width: 42px;
        height: 42px;
        display: grid;
        place-items: center;
        border: 1px solid #cfc4be;
        border-radius: 4px;
        background: #fff;
        color: #974900;

        &:disabled { opacity: 0.4; }
    }
}

.state-message {
    min-height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    color: #998f8a;

    &--error { color: #b42318; }
    button { border: 0; background: transparent; color: #974900; text-decoration: underline; }
}

.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.modal-notice {
    border-left: 3px solid #b45c0a;
    border-radius: 4px;
    padding: 10px 12px;
    background: #fff8e6;
    color: #6f4d14;
    font-size: 12px;
    line-height: 1.45;

    &--success {
        border-left-color: #237a3b;
        background: #eef8f1;
        color: #236334;
    }
}

@media (max-width: 760px) {
    .grouping-history__header { align-items: flex-start; }
    .grouping-history__identity > div { align-items: flex-start; flex-direction: column; gap: 5px; }
    .grouping-history__identity h1 { font-size: 19px; }
    .history-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .history-summary > div:nth-child(3) { border-left: 0; }
    .history-summary > div:nth-child(n + 3) { border-top: 1px solid #ece0db; }
    .history-panel > header { align-items: flex-start; }
    .history-entry__summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .history-entry__date { grid-column: 1 / -1; }
    .history-entry__summary > svg { justify-self: end; }
    .history-entry__delete { width: 46px; flex-basis: 46px; margin-left: 0; }
    .history-order--header { display: none; }
    .history-order { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .history-order > span:first-child { grid-column: 1 / -1; }
    .history-order > span:not(:first-child)::before {
        content: attr(data-label);
        display: block;
        margin-bottom: 2px;
        color: #998f8a;
        font-size: 9px;
        text-transform: uppercase;
    }
    .history-pagination { flex-wrap: wrap; justify-content: center; }
}

@media (max-width: 430px) {
    .history-panel > header { flex-direction: column; }
    .history-entry__summary { gap: 10px; padding: 12px; }
    .history-entry__orders { padding-right: 12px; padding-left: 12px; }
}
</style>
